import { Box, Tooltip } from '@mui/material';
import { differenceInYears, format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { capitalize, compact, find } from 'lodash';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import gender from 'common/constants/gender';
import { normalizePhoneNumber } from 'common/utils';
import { ShowDefaultPartnerContact } from 'common/utils/featureFlags';

import PatientLabelValue, { LABEL_VALUE_ROW_GAP } from './PatientLabelValue';
import PatientPartnersColumn from './PartnerContacts/PatientPartnersColumn';

const labelValueColumnSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: LABEL_VALUE_ROW_GAP,
  minWidth: 0,
};

const PatientInformation = ({ mpi }) => {
  const patientInfo = useSelector(({ patient }) => patient[mpi]);
  // The first name must exist; without it the patient is still loading.
  const isPatientLoaded = !!patientInfo?.firstName;
  const address = find(patientInfo?.addresses, { isDefault: true })?.address;

  const formattedDob =
    patientInfo?.dob && format(utcToZonedTime(new Date(patientInfo.dob), 'utc'), 'MM/dd/yyyy');
  const dateOfBirth =
    formattedDob &&
    `${formattedDob} (Age ${differenceInYears(new Date(), new Date(patientInfo.dob))})`;

  const mobilePhoneVerificationIcon = patientInfo?.isMobilePhoneVerified ? (
    <Tooltip title='Verified' placement='right'>
      <CheckCircleIcon color='success' sx={{ fontSize: 18 }} />
    </Tooltip>
  ) : (
    <Tooltip title='Not Verified' placement='right'>
      <ErrorOutlineIcon color='error' sx={{ fontSize: 18 }} />
    </Tooltip>
  );

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr minmax(300px, 1.15fr)',
        // One row exactly as tall as the card, so a long partner list scrolls instead of growing it.
        gridTemplateRows: 'minmax(0, 1fr)',
        columnGap: 4,
        height: '100%',
      }}
    >
      <Box sx={labelValueColumnSx}>
        <PatientLabelValue
          label='First Name'
          value={patientInfo?.firstName}
          isLoaded={isPatientLoaded}
        />
        <PatientLabelValue
          label='Middle Name'
          value={patientInfo?.middleName}
          isLoaded={isPatientLoaded}
        />
        <PatientLabelValue
          label='Last Name'
          value={patientInfo?.lastName}
          isLoaded={isPatientLoaded}
        />
        <PatientLabelValue label='Date of birth' value={dateOfBirth} isLoaded={isPatientLoaded} />
        <PatientLabelValue
          label='Gender'
          value={gender[patientInfo?.gender]}
          isLoaded={isPatientLoaded}
        />
        {/* With the default-contact switch off, only each partner's own email and phone show. */}
        {ShowDefaultPartnerContact && (
          <>
            <PatientLabelValue
              label='Email'
              value={patientInfo?.email}
              isLoaded={isPatientLoaded}
              defaultContactKind='email'
            />
            <PatientLabelValue
              label='Mobile Phone'
              value={normalizePhoneNumber(patientInfo?.mobilePhone)}
              isLoaded={isPatientLoaded}
              defaultContactKind='phone'
              adornment={mobilePhoneVerificationIcon}
            />
          </>
        )}
      </Box>

      <Box sx={labelValueColumnSx}>
        <PatientLabelValue
          label='Primary Spoken Language'
          value={capitalize(find(patientInfo?.languages, 'isPrimary')?.language)}
          isLoaded={isPatientLoaded}
        />
        <PatientLabelValue
          label='Address Line 1'
          value={address?.addressLine1}
          isLoaded={isPatientLoaded}
        />
        <PatientLabelValue
          label='Address Line 2'
          value={address?.addressLine2}
          isLoaded={isPatientLoaded}
        />
        <PatientLabelValue label='City' value={address?.city} isLoaded={isPatientLoaded} />
        <PatientLabelValue label='State' value={address?.state} isLoaded={isPatientLoaded} />
        <PatientLabelValue label='Zip' value={address?.zip} isLoaded={isPatientLoaded} />
      </Box>

      <PatientPartnersColumn
        mpi={mpi}
        patientName={compact([patientInfo?.firstName, patientInfo?.lastName]).join(' ')}
        partnerContacts={patientInfo?.contactsFromPartners}
        isPatientLoaded={isPatientLoaded}
      />
    </Box>
  );
};

PatientInformation.propTypes = {
  mpi: PropTypes.string.isRequired,
};

export default PatientInformation;
